import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchRoomTypes, createRoomType, updateRoomType, deleteRoomType, getCurrency,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Front Desk — room type catalogue (Standard, Deluxe, Suite ...).
const S = {
  en: {
    title: "Room types", subtitle: "Categories and default nightly rates",
    back: "Back to rooms", name: "Name", rate: "Nightly rate", cap: "Capacity",
    desc: "Description (optional)", add: "Add type", save: "Save", cancel: "Cancel",
    edit: "Edit", delete: "Delete", delete_confirm: "Remove this room type?",
    none: "No room types yet. Add one (e.g. Standard, Deluxe, Suite).",
    guests: "guests", name_req: "Name is required.", could_not_save: "Could not save.",
  },
  am: {
    title: "የክፍል ዓይነቶች", subtitle: "ምድቦች እና ነባሪ የሌሊት ዋጋዎች",
    back: "ወደ ክፍሎች ተመለስ", name: "ስም", rate: "የሌሊት ዋጋ", cap: "አቅም",
    desc: "መግለጫ (አማራጭ)", add: "ዓይነት ጨምር", save: "አስቀምጥ", cancel: "ሰርዝ",
    edit: "አርትዕ", delete: "አጥፋ", delete_confirm: "ይህን የክፍል ዓይነት ማስወገድ?",
    none: "እስካሁን የክፍል ዓይነቶች የሉም። አንድ ይጨምሩ (ለምሳሌ መደበኛ፣ ዲላክስ፣ ስዊት)።",
    guests: "እንግዶች", name_req: "ስም ያስፈልጋል።", could_not_save: "ማስቀመጥ አልተቻለም።",
  },
  om: {
    title: "Gosoota kutaa", subtitle: "Ramaddiiwwan fi gatii halkanii durii",
    back: "Gara kutaalee deebi'i", name: "Maqaa", rate: "Gatii halkanii", cap: "Baay'ina",
    desc: "Ibsa (filannoo)", add: "Gosa dabali", save: "Kaa'i", cancel: "Haqi",
    edit: "Gulaali", delete: "Haqi", delete_confirm: "Gosa kutaa kana haquu?",
    none: "Ammatti gosoonni kutaa hin jiru. Tokko dabali (fkn Standard, Deluxe, Suite).",
    guests: "keessummoota", name_req: "Maqaan barbaachisaa dha.", could_not_save: "Kaa'uu hin dandeenye.",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const empty = { name: "", base_rate: "", capacity: "", description: "" };

export default function RoomTypesPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";

  const [types, setTypes] = useState(null);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [err, setErr] = useState("");

  const load = () => fetchRoomTypes().then(setTypes).catch(() => setTypes([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) { setErr(t("name_req")); return; }
    const body = {
      name: form.name.trim(),
      base_rate: form.base_rate === "" ? null : Number(form.base_rate),
      capacity: form.capacity === "" ? null : Number(form.capacity),
      description: form.description.trim() || null,
    };
    try {
      if (editId) await updateRoomType(editId, body);
      else await createRoomType(body);
      setForm(empty);
      setEditId(null);
      load();
    } catch {
      setErr(t("could_not_save"));
    }
  };

  const startEdit = (tp) => {
    setEditId(tp.id);
    setForm({
      name: tp.name || "", base_rate: tp.base_rate ?? "",
      capacity: tp.capacity ?? "", description: tp.description || "",
    });
  };

  const remove = async (tp) => {
    if (!window.confirm(t("delete_confirm"))) return;
    setTypes((ts) => ts.filter((x) => x.id !== tp.id));
    try { await deleteRoomType(tp.id); } catch { load(); }
  };

  const inp = { padding: 8, borderRadius: 8, border: "1px solid #d8dee2" };

  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      <Link className="muted" to="/front-desk/rooms" style={{ fontSize: 13, textDecoration: "none" }}>← {t("back")}</Link>
      <h1 style={{ margin: "6px 0 2px" }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      <form onSubmit={submit} style={{ ...CARD, padding: 14, marginTop: 14, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("name")} style={{ ...inp, width: 150 }} />
        <input value={form.base_rate} onChange={(e) => setForm({ ...form, base_rate: e.target.value })} placeholder={t("rate")} type="number" min="0" style={{ ...inp, width: 120 }} />
        <input value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder={t("cap")} type="number" min="0" style={{ ...inp, width: 100 }} />
        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("desc")} style={{ ...inp, flex: 1, minWidth: 140 }} />
        <button className="btn primary small" type="submit">{editId ? t("save") : t("add")}</button>
        {editId && <button className="btn secondary small" type="button" onClick={() => { setEditId(null); setForm(empty); }}>{t("cancel")}</button>}
        {err && <span style={{ color: "#A32D2D", fontSize: 13, width: "100%" }}>{err}</span>}
      </form>

      {types == null ? (
        <p className="muted" style={{ marginTop: 14 }}>…</p>
      ) : types.length === 0 ? (
        <div style={{ ...CARD, padding: 20, marginTop: 14, textAlign: "center" }} className="muted">{t("none")}</div>
      ) : (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          {types.map((tp) => (
            <div key={tp.id} style={{ ...CARD, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{tp.name}</div>
                <div className="muted" style={{ fontSize: 13 }}>
                  {tp.base_rate != null ? formatPrice(tp.base_rate, cur) : "—"}
                  {tp.capacity != null && <> · {tp.capacity} {t("guests")}</>}
                  {tp.description && <> · {tp.description}</>}
                </div>
              </div>
              <button className="btn secondary small" onClick={() => startEdit(tp)}>{t("edit")}</button>
              <button onClick={() => remove(tp)} style={{ fontSize: 13, background: "none", border: "none", color: "#A32D2D", cursor: "pointer" }}>{t("delete")}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
